// src/data/buildings.ts
/**
 * Real Beaux Properties building data with Unsplash images
 */

export interface Unit {
  id: string;
  type: string;
  unitNumber: string;
  floor: number;
  price: number;
  sqft: number;
  availabilityDate: string;
  isAvailable: boolean;
  features?: string[];
}

export interface Building {
  id: string;
  slug: string;
  name: string;
  address: string;
  neighborhood: string;
  description: string;
  latitude: number;
  longitude: number;
  walkScore: number;
  transitScore: number;
  bikeScore: number;
  amenities: string[];
  leasingEmail: string;
  leasingPhone: string;
  heroImage: string;
  images: string[];
  units: Unit[];
}

export const BUILDINGS: Building[] = [
  {
    id: 'b1',
    slug: '66-broadway',
    name: '66 Broadway Avenue',
    address: '66 Broadway Ave, Toronto, ON M4P 1T6',
    neighborhood: 'Yonge & Eglinton',
    description: 'This clean and well located building is only steps away from subway, restaurants, entertainment, shopping. Come live in our newly renovated, professionally managed 20-story building.',
    latitude: 43.710734,
    longitude: -79.395245,
    walkScore: 95,
    transitScore: 98,
    bikeScore: 82,
    amenities: ['Pool', 'Gym', 'Elevators', 'Laundry', 'Rooftop Viewing Room', 'Underground Parking', 'On-site Superintendent'],
    leasingEmail: 'BeauxRentals@gmail.com',
    leasingPhone: '647-501-7368',
    heroImage: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80',
    images: [
      'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&q=80',
      'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80',
      'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80',
      'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=800&q=80',
      'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800&q=80',
    ],
    units: [
      { id: 'u1', type: 'Jr 1-BR', unitNumber: '801', floor: 8, price: 1850, sqft: 462, availabilityDate: '2025-01-15', isAvailable: true, features: ['Hardwood floors', 'Renovated kitchen', 'City views'] },
      { id: 'u2', type: '1-BR', unitNumber: '1204', floor: 12, price: 2150, sqft: 550, availabilityDate: '2025-02-01', isAvailable: true, features: ['Hardwood floors', 'In-unit laundry', 'Balcony'] },
      { id: 'u3', type: '2-BR', unitNumber: '1506', floor: 15, price: 2650, sqft: 850, availabilityDate: '2025-01-20', isAvailable: true, features: ['Corner unit', 'Panoramic views', 'Updated bathrooms'] },
      { id: 'u4', type: 'Bachelor', unitNumber: '503', floor: 5, price: 1550, sqft: 380, availabilityDate: '2025-01-10', isAvailable: true, features: ['Cozy layout', 'Bright south exposure'] },
    ],
  },
  {
    id: 'b2',
    slug: '25-wellesley',
    name: '25 Wellesley Street East',
    address: '25 Wellesley Street East, Toronto, ON M4Y 2S9',
    neighborhood: 'Yonge & Wellesley',
    description: 'Boutique building in the heart of downtown Toronto just steps from the subway. All apartments have in-suite laundry and central A/C. Units feature exposed brick and open concept design.',
    latitude: 43.664855,
    longitude: -79.383116,
    walkScore: 98,
    transitScore: 100,
    bikeScore: 89,
    amenities: ['In-suite Laundry', 'Central A/C', 'Elevators', 'Heat Included', 'Water Included', 'On-site Superintendent'],
    leasingEmail: 'BeauxRentals@gmail.com',
    leasingPhone: '647-501-7368',
    heroImage: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
    images: [
      'https://images.unsplash.com/photo-1560185007-c5ca9d2c014d?w=800&q=80',
      'https://images.unsplash.com/photo-1536376072261-38c75010e6c9?w=800&q=80',
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80',
      'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=800&q=80',
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80',
    ],
    units: [
      { id: 'u5', type: 'Bachelor', unitNumber: '201', floor: 2, price: 1650, sqft: 380, availabilityDate: '2025-01-10', isAvailable: true, features: ['Exposed brick', 'High ceilings'] },
      { id: 'u6', type: '1-BR', unitNumber: '305', floor: 3, price: 2100, sqft: 520, availabilityDate: '2025-02-15', isAvailable: true, features: ['In-suite laundry', 'Open concept', 'Central A/C'] },
      { id: 'u7', type: '2-BR', unitNumber: '402', floor: 4, price: 2800, sqft: 780, availabilityDate: '2025-03-01', isAvailable: true, features: ['Corner unit', 'Two bathrooms', 'City views'] },
    ],
  },
  {
    id: 'b3',
    slug: '95-havenbrook',
    name: '95 Havenbrook Boulevard',
    address: '95 Havenbrook Blvd, North York, ON M2J 1A9',
    neighborhood: 'Fairview Mall',
    description: 'Family building close to schools, Fairview Mall, Don Valley Parkway, and public transit. Outdoor pool and spacious, bright, freshly painted suites.',
    latitude: 43.768805,
    longitude: -79.347281,
    walkScore: 72,
    transitScore: 85,
    bikeScore: 65,
    amenities: ['Outdoor Pool', 'Elevators', 'Parking', 'Laundry', 'On-site Customer Service'],
    leasingEmail: 'BeauxRentals@gmail.com',
    leasingPhone: '647-501-7368',
    heroImage: 'https://images.unsplash.com/photo-1460317442991-0ec209397118?w=800&q=80',
    images: [
      'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80',
      'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80',
      'https://images.unsplash.com/photo-1560185007-cde436f6a4d0?w=800&q=80',
      'https://images.unsplash.com/photo-1598928506311-c55ez674c3d7?w=800&q=80',
      'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&q=80',
    ],
    units: [
      { id: 'u8', type: 'Jr 1-BR', unitNumber: '508', floor: 5, price: 1650, sqft: 480, availabilityDate: '2025-01-20', isAvailable: true, features: ['Freshly painted', 'Bright exposure'] },
      { id: 'u9', type: '1-BR', unitNumber: '712', floor: 7, price: 1850, sqft: 580, availabilityDate: '2025-02-01', isAvailable: true, features: ['Pool access', 'Balcony', 'Updated kitchen'] },
      { id: 'u10', type: '2-BR', unitNumber: '904', floor: 9, price: 2350, sqft: 820, availabilityDate: '2025-01-25', isAvailable: true, features: ['Family-sized', 'Two bedrooms', 'Storage locker'] },
    ],
  },
  {
    id: 'b4',
    slug: '7471-yonge',
    name: '7471 Yonge Street (Lampe Towers)',
    address: '7471 Yonge Street, Thornhill, ON L3T 2B9',
    neighborhood: 'Thornhill Village',
    description: 'Located at Yonge and Clark in the heart of Thornhill. Lampe Towers is a family building with outdoor pool close to all amenities. Spacious, freshly painted, totally renovated suites.',
    latitude: 43.809836,
    longitude: -79.422381,
    walkScore: 78,
    transitScore: 70,
    bikeScore: 58,
    amenities: ['Outdoor Pool', 'Elevators', 'Parking', 'Laundry', 'On-site Customer Service'],
    leasingEmail: 'BeauxRentals@gmail.com',
    leasingPhone: '647-501-7368',
    heroImage: 'https://images.unsplash.com/photo-1574362848149-11496d93a7c7?w=800&q=80',
    images: [
      'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80',
      'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=80',
      'https://images.unsplash.com/photo-1600121848594-d8644e57abab?w=800&q=80',
      'https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800&q=80',
      'https://images.unsplash.com/photo-1600573472591-ee6b68d14c68?w=800&q=80',
    ],
    units: [
      { id: 'u11', type: 'Bachelor', unitNumber: '102', floor: 1, price: 1450, sqft: 400, availabilityDate: '2025-01-15', isAvailable: true, features: ['Ground floor', 'Renovated', 'Affordable'] },
      { id: 'u12', type: '1-BR', unitNumber: '405', floor: 4, price: 1750, sqft: 550, availabilityDate: '2025-02-10', isAvailable: true, features: ['Freshly painted', 'Pool access', 'Balcony'] },
      { id: 'u13', type: '2-BR', unitNumber: '607', floor: 6, price: 2200, sqft: 780, availabilityDate: '2025-01-30', isAvailable: true, features: ['Renovated kitchen', 'Large bedrooms', 'Family-friendly'] },
    ],
  },
  {
    id: 'b5',
    slug: '60-mountview',
    name: '60 Mountview Avenue',
    address: '60 Mountview Ave, Toronto, ON M6P 2L4',
    neighborhood: 'High Park',
    description: 'A truly unique living experience. Nowhere else in Toronto has the same combination of subway access, green space, shopping, restaurants and schools. Steps from High Park and Bloor West Village.',
    latitude: 43.656667,
    longitude: -79.462258,
    walkScore: 89,
    transitScore: 94,
    bikeScore: 88,
    amenities: ['Elevators', 'Parking', 'Laundry', 'On-site Customer Service'],
    leasingEmail: 'BeauxRentals@gmail.com',
    leasingPhone: '647-501-7368',
    heroImage: 'https://images.unsplash.com/photo-1515263487990-61b07816b324?w=800&q=80',
    images: [
      'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800&q=80',
      'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=800&q=80',
      'https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?w=800&q=80',
      'https://images.unsplash.com/photo-1600585154084-4e5fe7c39198?w=800&q=80',
      'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?w=800&q=80',
    ],
    units: [
      { id: 'u14', type: 'Jr 1-BR', unitNumber: '201', floor: 2, price: 1550, sqft: 420, availabilityDate: '2025-01-18', isAvailable: true, features: ['Near High Park', 'Bright unit'] },
      { id: 'u15', type: '1-BR', unitNumber: '304', floor: 3, price: 1850, sqft: 540, availabilityDate: '2025-02-05', isAvailable: true, features: ['Updated kitchen', 'Hardwood floors', 'Balcony'] },
      { id: 'u16', type: '2-BR', unitNumber: '405', floor: 4, price: 2400, sqft: 750, availabilityDate: '2025-02-20', isAvailable: true, features: ['Corner unit', 'Park views', 'Two bathrooms'] },
    ],
  },
  {
    id: 'b6',
    slug: '135-fenelon',
    name: '135 Fenelon Drive',
    address: '135 Fenelon Drive, Toronto, ON M3A 3K7',
    neighborhood: 'DVP & York Mills',
    description: 'Family-friendly building on a quiet, tree-lined residential street. Moments from highway 401 and DVP. Features subsidized onsite daycare, close to Fairview Mall and public schools.',
    latitude: 43.762997,
    longitude: -79.338018,
    walkScore: 65,
    transitScore: 72,
    bikeScore: 55,
    amenities: ['Daycare', 'Elevators', 'Parking', 'Laundry', 'On-site Superintendent', 'Balconies'],
    leasingEmail: 'BeauxRentals@gmail.com',
    leasingPhone: '647-501-7368',
    heroImage: 'https://images.unsplash.com/photo-1554995207-c18c203602cb?w=800&q=80',
    images: [
      'https://images.unsplash.com/photo-1560185127-6a8c41f85b96?w=800&q=80',
      'https://images.unsplash.com/photo-1560185127-bdf4b64eabc8?w=800&q=80',
      'https://images.unsplash.com/photo-1600566752547-33b72c4f3f4c?w=800&q=80',
      'https://images.unsplash.com/photo-1600566753104-685f4f24cb4d?w=800&q=80',
      'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=80',
    ],
    units: [
      { id: 'u17', type: '1-BR', unitNumber: '308', floor: 3, price: 1750, sqft: 580, availabilityDate: '2025-01-22', isAvailable: true, features: ['Quiet street', 'Balcony', 'Storage'] },
      { id: 'u18', type: '2-BR', unitNumber: '512', floor: 5, price: 2150, sqft: 780, availabilityDate: '2025-02-01', isAvailable: true, features: ['Family-sized', 'Near daycare', 'Updated'] },
      { id: 'u19', type: '2-BR', unitNumber: '806', floor: 8, price: 2250, sqft: 820, availabilityDate: '2025-02-15', isAvailable: true, features: ['Higher floor', 'Great views', 'Large balcony'] },
    ],
  },
];

// Helper functions
export const getBuildingBySlug = (slug: string): Building | undefined =>
  BUILDINGS.find(b => b.slug === slug);

export const getBuildingById = (id: string): Building | undefined =>
  BUILDINGS.find(b => b.id === id);

export const getUnitById = (unitId: string): { unit: Unit; building: Building } | null => {
  for (const building of BUILDINGS) {
    const unit = building.units.find(u => u.id === unitId);
    if (unit) return { unit, building };
  }
  return null;
};

export const getAllUnits = (): (Unit & { building: Building })[] =>
  BUILDINGS.flatMap(b => b.units.map(u => ({ ...u, building: b })));

export const getAvailableUnits = (): (Unit & { building: Building })[] =>
  getAllUnits().filter(u => u.isAvailable);

// Get unit type stats for a building
export const getUnitTypeStats = (building: Building) => {
  const stats = building.units.reduce((acc, unit) => {
    if (!acc[unit.type]) {
      acc[unit.type] = { count: 0, minPrice: unit.price, maxPrice: unit.price };
    }
    acc[unit.type].count++;
    acc[unit.type].minPrice = Math.min(acc[unit.type].minPrice, unit.price);
    acc[unit.type].maxPrice = Math.max(acc[unit.type].maxPrice, unit.price);
    return acc;
  }, {} as Record<string, { count: number; minPrice: number; maxPrice: number }>);

  return stats;
};
